import React, {
    Fragment, useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback,
} from 'react';
import { Listbox, Transition, Portal } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'

export type Value = string | number;
interface MultiSelectOption {
    value: Value;
    label: string;
    disabled?: boolean;
    leftContent?: JSX.Element;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    defaultValue?: Value[];
    onChange: (value: Value[]) => void;
    inline?: boolean;
    placeholder?: string;
    className?: string;
    selectedClassName?: string;
    optionsClassName?: string;
    optionsWidth?: string | number;
    optionsHeight?: string | number;
};

type MenuCoords = { top: number; left: number; width: number };

type MultiSelectPanelProps = MultiSelectProps & {
    open: boolean;
    selected: MultiSelectOption[];
};

/** Inner panel so hooks stay valid outside the Listbox render-prop. */
function MultiSelectPanel(props: MultiSelectPanelProps) {
    const {
        open, options, placeholder, selected,
        className, selectedClassName, optionsClassName,
        inline, optionsWidth, optionsHeight,
    } = props;

    const buttonRef = useRef<HTMLButtonElement>(null);
    const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

    const updateMenuCoords = useCallback(() => {
        const button = buttonRef.current;
        if (!button)
            return;
        const rect = button.getBoundingClientRect();
        setMenuCoords({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setMenuCoords(null);
            return;
        }
        updateMenuCoords();
        window.addEventListener('resize', updateMenuCoords);
        // Capture scroll from overflow ancestors (tab panels, modal body, …).
        window.addEventListener('scroll', updateMenuCoords, true);
        return () => {
            window.removeEventListener('resize', updateMenuCoords);
            window.removeEventListener('scroll', updateMenuCoords, true);
        };
    }, [open, updateMenuCoords]);

    const menuWidth = typeof optionsWidth === 'number'
        ? optionsWidth
        : (menuCoords?.width ?? undefined);

    return (
        <div className={`relative mt-1 w-full ${className || ''}`}>
            <Listbox.Button
                ref={buttonRef}
                className={`relative flex min-h-[2.5rem] w-full cursor-default items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm ${inline ? '' : 'shadow-md'}`}
            >
                <span className={`block truncate ${selectedClassName || ''}`}>
                    {selected.map((s: MultiSelectOption) => s.label).join(', ') || placeholder}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                    />
                </span>
            </Listbox.Button>
            <Portal>
                <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options
                        style={menuCoords ? {
                            position: 'fixed',
                            top: menuCoords.top,
                            left: menuCoords.left,
                            width: menuWidth,
                        } : { display: 'none' }}
                        className={`z-[10000] max-h-60 ${optionsHeight || ''} overflow-auto no-scrollbar rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
                    >
                        {options.length === 0 ? (
                            <div className="cursor-default select-none py-2 px-4 text-gray-400">
                                —
                            </div>
                        ) : options.map((option: MultiSelectOption) => (
                            <Listbox.Option
                                key={option.value}
                                value={option.value}
                                disabled={!!option.disabled}
                                className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${optionsClassName || ''} ${
                                        active
                                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100'
                                            : 'text-gray-900 dark:text-gray-100'
                                    }`
                                }
                            >
                                {({ selected: isSelected }) => (
                                    <span className="flex justify-start items-center">
                                        {option.leftContent ? (
                                            <span className="absolute left-0 flex items-center">
                                                {option.leftContent}
                                            </span>
                                        ) : isSelected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        ) : null}
                                        <span className={`block ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                        </span>
                                    </span>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </Portal>
        </div>
    );
}

const MultiSelect = (props: MultiSelectProps) => {
    const { options, onChange, defaultValue } = props;

    const [value, setValue] = useState<Value[]>(defaultValue || []);

    useEffect(() => {
        setValue(defaultValue || []);
    }, [ defaultValue ]);

    const select = (next: Value[]) => {
        setValue(next);
        onChange && onChange(next);
    };

    const selected: MultiSelectOption[] = useMemo(() => {
        return options.filter((option: MultiSelectOption) => value.includes(option.value));
    }, [options, value]);

    return (
        <Listbox value={value} onChange={select} multiple>
            {({ open }) => (
                <MultiSelectPanel
                    {...props}
                    open={open}
                    selected={selected}
                />
            )}
        </Listbox>
    )
};

export default MultiSelect;
